import { z } from "zod";
import { FigureSlider } from "./figure.js";

/**
 * 3D dans les leçons, deux sortes :
 *  - « solid3d » : solide généré par le code (maths, physique), décrit par quelques nombres
 *    ou curseurs. Ne pèse rien, se traduit comme le reste, fonctionne hors ligne.
 *  - « scene3d » : modèle GLB réalisé à la main (SVT…), avec des points annotés traduits.
 */

export const SOLID_SHAPES = {
  cube: { label: "Cube", dims: ["c"], volume: "c³" },
  pave: { label: "Pavé droit", dims: ["L", "l", "h"], volume: "L × l × h" },
  pyramide: { label: "Pyramide à base carrée", dims: ["c", "h"], volume: "(c² × h) ÷ 3" },
  cone: { label: "Cône de révolution", dims: ["r", "h"], volume: "(π × r² × h) ÷ 3" },
  cylindre: { label: "Cylindre", dims: ["r", "h"], volume: "π × r² × h" },
  sphere: { label: "Boule", dims: ["r"], volume: "(4 × π × r³) ÷ 3" },
} as const;
export type SolidShape = keyof typeof SOLID_SHAPES;

/** Une dimension vaut un nombre fixe ou suit un curseur que l'élève manipule. */
const DimValue = z.union([z.number().positive().max(20), z.object({ slider: z.string().min(1).max(8) })]);

export const SolidSpec = z.object({
  kind: z.literal("solid"),
  shape: z.enum(Object.keys(SOLID_SHAPES) as [SolidShape, ...SolidShape[]]),
  dims: z.record(DimValue),
  sliders: z.array(FigureSlider).max(3).default([]),
  show: z.object({
    edges: z.boolean().default(true),
    height: z.boolean().default(true), // hauteur en pointillés (pyramide, cône…)
    volume: z.boolean().default(true), // formule et valeur du volume, mises à jour en direct
  }).default({}),
  unit: z.string().max(6).default("cm"),
}).superRefine((s, ctx) => {
  const names = new Set(s.sliders.map((x) => x.name));
  for (const d of SOLID_SHAPES[s.shape].dims) {
    const v = s.dims[d];
    if (v === undefined) ctx.addIssue({ code: "custom", path: ["dims", d], message: `Dimension « ${d} » manquante pour ce solide` });
    else if (typeof v === "object" && !names.has(v.slider)) ctx.addIssue({ code: "custom", path: ["dims", d], message: `La dimension « ${d} » suit un curseur inexistant : ${v.slider}` });
  }
  for (const sl of s.sliders) if (sl.min <= 0) ctx.addIssue({ code: "custom", path: ["sliders"], message: `Le curseur ${sl.name} règle une longueur : son minimum doit être positif` });
});
export type SolidSpec = z.infer<typeof SolidSpec>;

/** Valeurs des dimensions pour des curseurs donnés. */
export function solidDims(s: SolidSpec, params: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const d of SOLID_SHAPES[s.shape].dims) {
    const v = s.dims[d];
    out[d] = typeof v === "number" ? v : params[v?.slider ?? ""] ?? s.sliders.find((x) => x.name === v?.slider)?.initial ?? 1;
  }
  return out;
}

export function solidVolume(shape: SolidShape, d: Record<string, number>): number {
  switch (shape) {
    case "cube": return d.c ** 3;
    case "pave": return d.L * d.l * d.h;
    case "pyramide": return (d.c ** 2 * d.h) / 3;
    case "cone": return (Math.PI * d.r ** 2 * d.h) / 3;
    case "cylindre": return Math.PI * d.r ** 2 * d.h;
    case "sphere": return (4 * Math.PI * d.r ** 3) / 3;
  }
}

export const SOLID_PRESETS: { id: string; label: string; spec: SolidSpec }[] = [
  {
    id: "pyramide", label: "Pyramide : hauteur variable",
    spec: { kind: "solid", shape: "pyramide", dims: { c: 4, h: { slider: "h" } }, sliders: [{ name: "h", label: { fr: "Hauteur h", wo: "Kawe h" }, min: 1, max: 8, step: 0.5, initial: 3 }], show: { edges: true, height: true, volume: true }, unit: "cm" },
  },
  {
    id: "cone", label: "Cône : rayon et hauteur",
    spec: { kind: "solid", shape: "cone", dims: { r: { slider: "r" }, h: { slider: "h" } }, sliders: [{ name: "r", label: { fr: "Rayon r" }, min: 0.5, max: 4, step: 0.5, initial: 2 }, { name: "h", label: { fr: "Hauteur h" }, min: 1, max: 8, step: 0.5, initial: 4 }], show: { edges: true, height: true, volume: true }, unit: "cm" },
  },
  {
    id: "cube", label: "Cube : arête variable",
    spec: { kind: "solid", shape: "cube", dims: { c: { slider: "c" } }, sliders: [{ name: "c", label: { fr: "Arête c" }, min: 1, max: 5, step: 0.5, initial: 2 }], show: { edges: true, height: false, volume: true }, unit: "cm" },
  },
];

/** Point annoté d'un modèle 3D : position sur la surface (repère du modèle) et textes par langue. */
export const Hotspot = z.object({
  id: z.string().regex(/^[a-z0-9-]{1,40}$/),
  position: z.tuple([z.number().finite(), z.number().finite(), z.number().finite()]),
  normal: z.tuple([z.number().finite(), z.number().finite(), z.number().finite()]).default([0, 1, 0]),
  label: z.record(z.string().max(60)).refine((m) => !!m.fr, "Libellé en français obligatoire"),
  text: z.record(z.string().max(400)).default({}),
});
export type Hotspot = z.infer<typeof Hotspot>;

/** Plafonds pour les téléphones d'entrée de gamme (Galaxy A06, itel A70). */
export const MODEL_LIMITS = { maxBytes: 5 * 1024 * 1024, maxTriangles: 100_000, maxTextureSize: 2048, maxHotspots: 20 } as const;

/**
 * Noms de parties d'un modèle 3D : seules les parties vraiment nommées par l'auteur servent de légende.
 * Les outils 3D laissent des noms techniques (« Sphere.008_0 », « Object_4 », « Cube.001 », « Material.015 »)
 * qui ne veulent rien dire pour un élève : ils sont écartés par le lecteur, et l'import tente de les remplacer.
 */
const GENERIC_PART = /^(?:Sketchfab_model|GLTF_SceneRootNode|Root(?:Node)?|Scene|Object|Mesh|Node|Empty|Group|Sphere|Icosphere|UVSphere|Cube|Roundcube|Cylinder|Cone|Plane|Circle|Torus|BezierCurve|Curve|NurbsPath|Text|Armature|Camera|Light|defaultMaterial|Material|material|mat)(?:[._ -]?\d+)*(?:[._]\d+)*$/i;
export const isGenericPartName = (name: string) => !name.trim() || GENERIC_PART.test(name.trim());
